import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { trackEvent } from "@/api";
import { useAuth } from "@/auth";
import { Zap, Check, ArrowRight, Dumbbell, LineChart, MessageCircle, Home, X, Sparkles, RotateCcw, Flame, Star, Instagram } from "lucide-react";
import { PaymentBadges } from "@/components/PaymentBadges";

const STEPS = [
  { icon: Zap, title: "Scegli il tuo pacchetto", text: "AD START, AD EVOLVE, AD BOOST o AD ELITE: attivazione immediata dopo il pagamento sicuro con Stripe." },
  { icon: Dumbbell, title: "Ricevi il tuo programma", text: "Il coach costruisce il tuo piano personalizzato: fasi, settimane e schede. Ogni esercizio con video-esecuzione animata." },
  { icon: LineChart, title: "Allenati e migliora", text: "Traccia workout, peso, misure e foto. Il coach segue i tuoi progressi e adatta il percorso." },
];

const FAQS = [
  { q: "Cosa succede dopo l'acquisto?", a: "Ricevi subito l'accesso all'app. Il coach analizza il tuo questionario iniziale e prepara il tuo programma personalizzato entro pochi giorni." },
  { q: "Le rate sono davvero senza interessi?", a: "Sì: con PayPal o Klarna paghi in 3 rate mensili dello stesso importo, senza costi aggiuntivi. La disponibilità dipende dall'idoneità del tuo conto." },
  { q: "Posso cambiare pacchetto in corso?", a: "Sì, puoi passare a un programma superiore in qualsiasi momento: scrivi al coach dalla chat in app e aggiorniamo il tuo piano." },
  { q: "Cosa succede quando il pacchetto scade?", a: "L'accesso ai contenuti viene sospeso finché non rinnovi. Storico allenamenti, misure e foto restano salvati e li ritrovi al rinnovo." },
  { q: "Serve attrezzatura da palestra?", a: "Il programma viene costruito in base all'attrezzatura che hai: palestra completa, home gym o corpo libero. Lo indichi nel questionario iniziale." },
];

const QUIZ_DURATIONS = [
  { label: "1 mese", sub: "Per iniziare", key: "pack_monthly" },
  { label: "3 mesi", sub: "Risultati visibili", key: "pack_quarterly" },
  { label: "6 mesi", sub: "Trasformazione completa", key: "pack_semiannual" },
  { label: "1 anno", sub: "Massimo impegno", key: "pack_yearly" },
];

const QUIZ_PLACES = [
  { label: "A casa", sub: "Corpo libero, zero attrezzi", key: "prog_casa" },
  { label: "In palestra", sub: "Macchine e pesi liberi", key: "prog_palestra" },
];

function QuizOption({ label, sub, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="kc-card kc-card-hover p-5 text-left w-full transition-transform hover:-translate-y-0.5">
      <span className="block font-bold">{label}</span>
      {sub && <span className="block text-sm text-muted-foreground mt-1">{sub}</span>}
    </button>
  );
}

function PackageQuiz({ packages, buyTo, onClose }) {
  const [step, setStep] = useState(0);
  const [coach, setCoach] = useState(null);
  const [resultKey, setResultKey] = useState(null);
  const pack = packages.find((p) => p.lookup_key === resultKey);

  return (
    <div className="kc-card p-7 sm:p-10 w-full border-primary/40 relative" data-testid="quiz-section">
      {onClose && (
        <button data-testid="quiz-close" onClick={onClose}
          className="absolute top-4 right-4 kc-btn-ghost !p-2" aria-label="Chiudi">
          <X className="w-5 h-5" />
        </button>
      )}
      <p className="kc-eyebrow mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" />Quale fa per te?</p>
        {step === 0 && (
          <>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-3">Non sai quale scegliere?</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Rispondi a 2 domande e ti diciamo subito il programma giusto per te: coaching personalizzato o scheda pronta.
            </p>
            <button data-testid="quiz-start-button" onClick={() => setStep(1)}
              className="kc-btn-primary !px-7 !py-3 inline-flex items-center gap-2">
              Inizia il quiz <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {step === 1 && (
          <>
            <p className="text-xs text-muted-foreground mb-2">Domanda 1 di 2</p>
            <h3 className="font-black text-xl uppercase tracking-tight mb-6">Vuoi essere seguito personalmente da un coach?</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <QuizOption testid="quiz-q1-coach" label="Sì, voglio un percorso su misura"
                sub="Programma personalizzato, chat e check-in col coach"
                onClick={() => { setCoach(true); setStep(2); }} />
              <QuizOption testid="quiz-q1-self" label="No, mi alleno in autonomia"
                sub="Scheda già pronta, inizi subito da solo"
                onClick={() => { setCoach(false); setStep(2); }} />
            </div>
          </>
        )}
        {step === 2 && coach && (
          <>
            <p className="text-xs text-muted-foreground mb-2">Domanda 2 di 2</p>
            <h3 className="font-black text-xl uppercase tracking-tight mb-6">Per quanto tempo vuoi essere seguito?</h3>
            <div className="grid grid-cols-2 gap-3">
              {QUIZ_DURATIONS.map((d) => (
                <QuizOption key={d.key} testid={`quiz-q2-${d.key}`} label={d.label} sub={d.sub}
                  onClick={() => { setResultKey(d.key); setStep(3); }} />
              ))}
            </div>
          </>
        )}
        {step === 2 && !coach && (
          <>
            <p className="text-xs text-muted-foreground mb-2">Domanda 2 di 2</p>
            <h3 className="font-black text-xl uppercase tracking-tight mb-6">Dove ti alleni?</h3>
            <div className="grid grid-cols-2 gap-3">
              {QUIZ_PLACES.map((d) => (
                <QuizOption key={d.key} testid={`quiz-q2-${d.key}`} label={d.label} sub={d.sub}
                  onClick={() => { setResultKey(d.key); setStep(3); }} />
              ))}
            </div>
          </>
        )}
        {step === 3 && pack && (
          <div data-testid="quiz-result">
            <p className="text-xs text-muted-foreground mb-2">Il nostro consiglio</p>
            <h3 className="font-black text-2xl uppercase tracking-tight">{pack.name}</h3>
            <p className="text-primary font-mono text-3xl font-black my-3" data-testid="quiz-result-price">€{(pack.amount / 100).toFixed(0)}</p>
            <p className="text-sm text-muted-foreground mb-6">
              {pack.self_service
                ? "Scheda pronta di 8 settimane: acquisti e ti alleni subito, in autonomia."
                : `${pack.days} giorni di coaching personalizzato: programma su misura, chat e check-in col coach.`}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={buyTo(pack.lookup_key)} data-testid="quiz-result-cta"
                className="kc-btn-primary !px-7 !py-3 inline-flex items-center gap-2">
                Inizia con {pack.name} <ArrowRight className="w-4 h-4" />
              </Link>
              <button data-testid="quiz-restart" onClick={() => { setStep(0); setCoach(null); setResultKey(null); }}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border font-bold text-sm hover:border-primary hover:text-primary transition-colors">
                <RotateCcw className="w-4 h-4" /> Rifai il quiz
              </button>
            </div>
          </div>
        )}
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [quizOpen, setQuizOpen] = useState(false);
  const buyTo = (key) => (user?.role === "client" ? "/app/packages" : `/register?pack=${key}`);

  useEffect(() => {
    trackEvent("landing_view");
    api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <header className="sticky top-0 z-20 bg-[#FDFBFB]/90 backdrop-blur-md border-b border-border/60">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-3">
          <img src="/adtraining-h-onlight.png" alt="AD TRAINING PROGRAM" className="h-9 w-auto" data-testid="landing-logo" />
          <div className="flex items-center gap-4">
            <Link to="/login" data-testid="landing-login-button"
              className="kc-btn-primary !px-5 !py-2 text-sm inline-flex items-center gap-2">
              Accedi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden" style={{ background: "#FDFBFB" }}>
        <picture className="contents">
          <source media="(max-width: 640px)" srcSet="/hero-warm-mobile.jpg" />
          <img src="/hero-warm.jpg" alt="" aria-hidden="true" data-testid="hero-training-photo"
            className="absolute inset-0 w-full h-full object-cover object-[50%_45%] sm:object-[60%_30%] opacity-[0.5] sm:opacity-[0.6] pointer-events-none select-none" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBFB] via-[#FDFBFB]/70 to-[#FDFBFB]/20 sm:bg-gradient-to-r sm:from-[#FDFBFB] sm:via-[#FDFBFB]/85 sm:to-[#FDFBFB]/15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBFB]/45 via-transparent to-[#FDFBFB]" />
        <div className="relative max-w-6xl mx-auto px-5 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <p className="kc-eyebrow mb-4 kc-animate">Coaching online · AD Training Program</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] max-w-3xl kc-animate text-[#1C181A]" style={{ animationDelay: "0.08s" }}>
            Il tuo percorso.<br />Il tuo coach.<br /><span className="text-primary">Ovunque.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#1C181A]/70 mt-6 max-w-xl kc-animate" style={{ animationDelay: "0.16s" }}>
            Programmi di allenamento e nutrizione personalizzati, video-esecuzione animata di oltre 70 esercizi, routine di stretching, tracking dei progressi, meditazione guidata e supporto diretto del coach — tutto in un'unica app.
          </p>
          <p className="mt-5 text-sm font-semibold kc-animate text-[#1C181A]" style={{ animationDelay: "0.2s" }} data-testid="hero-price-hint">
            Coaching a rate da <span className="text-primary font-black text-lg">33€/mese</span> · Schede pronte a rate da <span className="text-primary font-black text-lg">13€/mese</span> — 3 rate senza interessi
          </p>
          <div className="flex flex-wrap gap-3 mt-10 kc-animate" style={{ animationDelay: "0.24s" }}>
            <Link to="/register" data-testid="landing-cta-trial" className="kc-btn-primary !px-7 !py-3 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Prova gratis 7 giorni
            </Link>
            <a href="#pacchetti" data-testid="landing-cta-packages"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-[#1C181A]/20 font-bold text-sm text-[#1C181A] hover:border-primary hover:text-primary transition-colors">
              Scopri i programmi <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section id="come-funziona" className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
        <p className="kc-eyebrow mb-3 kc-animate">Come funziona</p>
        <h2 className="text-base md:text-lg font-bold uppercase tracking-wide mb-10">Tre passi verso i tuoi risultati</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="kc-card p-6 kc-animate" style={{ animationDelay: `${0.1 * i}s` }} data-testid={`landing-step-${i + 1}`}>
              <span className="w-12 h-12 rounded-full bg-primary/12 grid place-items-center mb-4">
                <s.icon className="w-6 h-6 text-primary" />
              </span>
              <p className="font-bold mb-1.5">{i + 1}. {s.title}</p>
              <p className="text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 pb-16 sm:pb-24" data-testid="landing-testimonials">
          <p className="kc-eyebrow mb-3">Risultati reali</p>
          <h2 className="text-base md:text-lg font-bold uppercase tracking-wide mb-10">Prima e dopo con ADTRAINING</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((tm, i) => (
              <div key={tm._id} className="kc-card overflow-hidden kc-animate" style={{ animationDelay: `${0.1 * i}s` }} data-testid={`landing-testimonial-${tm._id}`}>
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img src={`/api/testimonials/${tm._id}/photo/before`} alt="Prima" className="w-full aspect-[3/4] object-cover" />
                    <span className="absolute bottom-2 left-2 kc-badge bg-background/85 text-foreground">Prima</span>
                  </div>
                  <div className="relative">
                    <img src={`/api/testimonials/${tm._id}/photo/after`} alt="Dopo" className="w-full aspect-[3/4] object-cover" />
                    <span className="absolute bottom-2 left-2 kc-badge bg-primary text-primary-foreground">Dopo</span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="font-bold">{tm.name}</p>
                  <p className="text-sm text-primary font-semibold mt-0.5">{tm.result}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {reviews.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 pb-16 sm:pb-24" data-testid="landing-reviews">
          <p className="kc-eyebrow mb-3">Dicono di noi</p>
          <h2 className="text-base md:text-lg font-bold uppercase tracking-wide mb-2">Le recensioni dei clienti</h2>
          {reviews.length >= 3 && (
            <p className="text-sm text-muted-foreground mb-8 flex items-center gap-2" data-testid="reviews-average">
              <span className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= Math.round(reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length) ? "fill-primary text-primary" : "text-border"}`} />
                ))}
              </span>
              <span className="font-bold text-foreground">{(reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1)}/5</span>
              · {reviews.length} recensioni
            </p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((rv, i) => (
              <div key={rv._id} className="kc-card p-6 kc-animate" style={{ animationDelay: `${0.1 * i}s` }} data-testid={`landing-review-${rv._id}`}>
                <div className="flex gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= (rv.rating || 5) ? "fill-primary text-primary" : "text-border"}`} />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-4">“{rv.text}”</p>
                <p className="font-bold text-sm">{rv.name}</p>
                {rv.package && <p className="text-xs text-primary font-semibold mt-0.5">{rv.package}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="pacchetti" className="max-w-6xl mx-auto px-5 pb-20 sm:pb-28">
        <p className="kc-eyebrow !text-sm sm:!text-base mb-3">Coaching personalizzato</p>
        <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-3">Scegli il tuo percorso</h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-xl">
          Pagamento unico, nessun rinnovo automatico. Accesso completo all'app per tutta la durata del pacchetto.
          <span className="block mt-2 text-foreground font-semibold" data-testid="landing-installments">
            Disponibile anche in 3 rate senza interessi con PayPal o Klarna.
          </span>
        </p>
        <div className="mb-8"><PaymentBadges /></div>
        <button data-testid="landing-quiz-cta" onClick={() => setQuizOpen(true)}
          className="mb-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/40 bg-primary/5 text-sm font-bold text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
          <Sparkles className="w-4 h-4" /> Non sai quale scegliere? Fai il quiz di 30 secondi
        </button>
        <div className="kc-card p-6 mb-10 border-accent/50 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="landing-trial-banner">
          <div className="flex-1">
            <p className="kc-eyebrow !text-accent mb-1">Prova gratuita</p>
            <p className="font-black text-lg uppercase tracking-tight">7 giorni gratis, nessuna carta richiesta</p>
            <p className="text-sm text-muted-foreground mt-1">Esplora l'app: libreria esercizi, routine di stretching e meditazione. Poi scegli il tuo percorso con il coach.</p>
          </div>
          <Link to="/register" data-testid="landing-trial-cta"
            className="kc-btn-primary !px-6 !py-2.5 text-center inline-flex items-center justify-center gap-2 shrink-0">
            Inizia la prova <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {packages.filter((p) => !p.self_service).map((p, i) => (
            <div key={p.lookup_key} data-testid={`landing-package-${p.lookup_key}`}
              className={`kc-card p-7 flex flex-col kc-animate relative ${i === 1 ? "border-primary/60 lg:-translate-y-2 shadow-xl" : ""}`}
              style={{ animationDelay: `${0.1 * i}s` }}>
              {i === 1 && (
                <span className="absolute -top-3 left-6 kc-badge bg-primary text-primary-foreground shadow-lg inline-flex items-center gap-1" data-testid="landing-popular-badge">
                  <Star className="w-3 h-3 fill-current" /> Più scelto
                </span>
              )}
              <span className="kc-badge bg-accent/15 text-accent mb-3 self-start" data-testid={`landing-coachbadge-${p.lookup_key}`}>Con coach dedicato</span>
              <Dumbbell className={`w-6 h-6 mb-3 ${i === 1 ? "text-primary" : "text-accent"}`} />
              <h3 className="font-black text-xl uppercase tracking-tight">{p.name}</h3>
              <p className="text-sm text-muted-foreground">{p.days} giorni di accesso</p>
              <p className="font-mono text-4xl font-black my-5" data-testid={`landing-price-${p.lookup_key}`}>
                €{(p.amount / 100).toFixed(0)}
              </p>
              <p className="text-xs text-muted-foreground -mt-4 mb-5" data-testid={`landing-installments-${p.lookup_key}`}>
                .. anche a rate da <span className="font-bold text-foreground">€{(p.amount / 300).toFixed(0)}/mese</span> · 3 rate senza interessi
              </p>
              <ul className="space-y-2 mb-8">
                {(p.features || []).slice(0, 5).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link to={buyTo(p.lookup_key)} data-testid={`landing-buy-${p.lookup_key}`}
                className="kc-btn-primary w-full !py-2.5 mt-auto text-center inline-flex items-center justify-center gap-2">
                Acquista {p.name} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-8 flex items-center gap-2" data-testid="landing-contact-note">
          <MessageCircle className="w-4 h-4 text-primary shrink-0" />
          Registrati e scegli il tuo pacchetto: dopo l'acquisto il coach prepara il tuo programma personalizzato. Domande?
          <a href="mailto:admethodstudio@gmail.com" className="text-primary font-semibold hover:underline">admethodstudio@gmail.com</a>
        </p>
      </section>

      {packages.some((p) => p.self_service) && (
        <section id="programmi-pronti" className="max-w-6xl mx-auto px-5 pb-20 sm:pb-28">
          <p className="kc-eyebrow !text-sm sm:!text-base mb-3">Programmi Ready-to-Train</p>
          <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-3">Inizia oggi: a casa o in palestra</h2>
          <p className="text-sm text-muted-foreground mb-8 max-w-xl">
            Schede di 8 settimane già pronte, create dal coach. Acquisti, entri in app e ti alleni subito — nessuna attesa, nessun questionario.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 max-w-3xl" data-testid="selfprogram-specs">
            {[
              { icon: "🗓", title: "8 settimane", sub: "2 mesi di progressione già impostata" },
              { icon: "⏱", title: "3 allenamenti a settimana", sub: "Sedute da 30-45 minuti" },
              { icon: "▶", title: "Video di ogni esercizio", sub: "Esecuzione animata nell'app" },
              { icon: "↓", title: "Tracker + PDF", sub: "Registri i carichi in app o scarichi la scheda" },
            ].map((s) => (
              <div key={s.title} className="kc-card p-4 text-center">
                <span className="block text-lg mb-1 text-primary font-black">{s.icon}</span>
                <span className="block text-xs font-bold uppercase tracking-wide">{s.title}</span>
                <span className="block text-[11px] text-muted-foreground mt-1">{s.sub}</span>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch max-w-5xl">
            {packages.filter((p) => p.self_service).map((p) => (
              <div key={p.lookup_key} data-testid={`landing-selfprogram-${p.lookup_key}`}
                className="kc-card p-7 flex flex-col kc-animate border-primary/40">
                <span className="kc-badge bg-secondary text-secondary-foreground mb-4 self-start" data-testid={`landing-selfbadge-${p.lookup_key}`}>
                  Senza coach · fai da te, inizi subito
                </span>
                {p.lookup_key === "prog_casa"
                  ? <Home className="w-6 h-6 mb-3 text-primary" />
                  : p.lookup_key === "prog_booty"
                    ? <Flame className="w-6 h-6 mb-3 text-primary" />
                    : <Dumbbell className="w-6 h-6 mb-3 text-primary" />}
                <h3 className="font-black text-xl uppercase tracking-tight">{p.name}</h3>
                <p className="text-sm text-muted-foreground">8 settimane · {p.days} giorni di accesso</p>
                <p className="font-mono text-4xl font-black my-5" data-testid={`landing-selfprice-${p.lookup_key}`}>
                  €{(p.amount / 100).toFixed(0)}
                </p>
                <p className="text-xs font-semibold -mt-3 mb-1">Paghi una volta: la scheda è già pronta</p>
                <p className="text-xs text-muted-foreground mb-5" data-testid={`landing-installments-${p.lookup_key}`}>
                  .. anche a rate da <span className="font-bold text-foreground">€{(p.amount / 300).toFixed(0)}/mese</span> · 3 rate senza interessi
                </p>
                <ul className="space-y-2 mb-4">
                  {(p.features || []).slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2 mb-8" data-testid={`landing-selfexcluded-${p.lookup_key}`}>
                  {["Programma personalizzato su di te", "Chat diretta col coach"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground/70">
                      <X className="w-4 h-4 shrink-0 mt-0.5" /> <span className="line-through">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={buyTo(p.lookup_key)} data-testid={`landing-buy-${p.lookup_key}`}
                  className="kc-btn-primary w-full !py-2.5 mt-auto text-center inline-flex items-center justify-center gap-2">
                  Inizia subito <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-5 pb-20 sm:pb-28" data-testid="landing-faq">
        <p className="kc-eyebrow mb-3">Domande frequenti</p>
        <h2 className="text-base md:text-lg font-bold uppercase tracking-wide mb-8">Tutto quello che vuoi sapere</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={f.q} className="kc-card px-5 py-4 group" data-testid={`faq-item-${i}`}>
              <summary className="font-bold cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-3">
                {f.q}
                <ArrowRight className="w-4 h-4 text-primary shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="text-sm text-muted-foreground mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {quizOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/85 backdrop-blur-sm"
          data-testid="quiz-modal" onClick={() => setQuizOpen(false)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <PackageQuiz packages={packages} buyTo={buyTo} onClose={() => setQuizOpen(false)} />
          </div>
        </div>
      )}

      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/adtraining-h-onlight.png" alt="AD TRAINING PROGRAM" className="h-8 w-auto opacity-80" />
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <span data-testid="footer-vat">Zandona Donatella · P.IVA 04278140134</span>
            <Link to="/privacy" data-testid="landing-privacy-link" className="hover:text-foreground">Privacy Policy</Link>
            <Link to="/termini" data-testid="landing-terms-link" className="hover:text-foreground">Termini</Link>
            <Link to="/cookie-policy" data-testid="landing-cookies-link" className="hover:text-foreground">Cookie Policy</Link>
            <Link to="/login" className="hover:text-foreground">Area riservata</Link>
            <a href="https://instagram.com/donatellazandona_coach" target="_blank" rel="noopener noreferrer"
              data-testid="footer-instagram-link" aria-label="Instagram @donatellazandona_coach"
              className="text-primary hover:opacity-80 transition-opacity">
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="border-t border-border/40 py-4 text-center text-[11px] text-muted-foreground" data-testid="footer-copyright">
          © 2026 AD TRAINING PROGRAM · Tutti i diritti riservati
        </div>
      </footer>
    </div>
  );
}
